import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { screenMetricColumnLabel } from "./metricScreenHeadings";
import { withoutUzsLabel } from "./currencyLabels";

// Rename final workbook headers after every existing formatter has finished.
// Data, formula operations, styles, dimensions, filters and worksheet order stay
// intact. Structured references follow their renamed native table columns.
const plain = (s: string) => s.replace(/&(?:amp|lt|gt|quot|apos);/g, entity => ({ "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&apos;": "'" })[entity]!);
const xml = (s: string) => s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&apos;");
const matchKey = (s: string) => s.normalize("NFC").toLowerCase().replaceAll("\u0307", "").replace(/[‘’]/g, "'").replace(/\s+/g, " ").trim();
const aliases = new Map<string, string>();
const add = (key: string, names: string[]) => names.forEach(name => aliases.set(matchKey(name), key));
add("sales", ["Satış", "Net Ciro", "Ciro", "Sales", "Net Revenue", "Revenue", "Продажи", "Чистая выручка", "Выручка (нетто)", "Выручка", "Savdo", "Sotuv", "Sof tushum"]);
add("lySales", ["LY Satış", "LY Ciro", "Geçen Yıl Ciro", "LY Sales", "LY Revenue", "Last Year Revenue", "Last Year Sales", "Выручка ПГ", "Выручка прошлого года", "Продажи LY", "O‘tgan yil tushumi", "LY savdo"]);
add("salesChange", ["LY Satış Değişim %", "vs LY Satış", "Ciro %", "Ciro Değişimi %", "Ciro Değişim %", "LY Sales Change %", "vs LY Sales", "Revenue Change %", "Изменение продаж LY %", "Изменение выручки %", "vs ПГ Продажи", "LY savdo o‘zgarishi %", "Tushum o‘zgarishi %"]);
add("basket", ["Sepet Ort.", "Sepet", "Sepet Ortalaması", "ATV", "Avg. Basket", "Average Basket", "Basket", "Средний чек", "O‘rtacha chek"]);
add("previousAtv", ["LY Sepet Ort.", "LY ATV", "LY Avg. Basket"]);
add("atvChange", ["vs LY ATV", "LY Sepet Ort. Değişim %", "LY Avg. Basket Change %"]);
add("unitPrice", ["Birim Fiyat", "Ort. Birim Fiyat", "Ortalama Birim Fiyat", "Ortalama Fiyat", "AVP", "Unit Price", "Avg. Unit Price", "Average Unit Price", "Цена за шт.", "Цена за единицу", "Средняя цена за шт.", "Bir dona narxi", "Dona narxi", "O‘rtacha dona narxi"]);
add("lyUnitPrice", ["LY Birim Fiyat", "LY AVP", "LY Unit Price", "Цена за шт. LY", "LY dona narxi"]);
add("avpChange", ["vs LY AVP", "LY Birim Fiyat Değişim %", "LY Unit Price Change %"]);
add("receipts", ["Müşteri", "Müşteri (Fiş)", "Müşteri (Fiş) Sayısı", "Fiş Sayısı", "Customers", "Customer (Receipt)", "Receipt Count", "Receipts", "Покупатели", "Покупатель (чек)", "Чеки", "Количество чеков", "Mijozlar", "Mijoz (chek)", "Cheklar soni"]);
add("lyReceipts", ["LY Müşteri (Fiş)", "LY Fiş Sayısı", "LY Customer (Receipt)", "LY Receipts"]);
add("receiptsChange", ["vs LY Müşteri", "LY Müşteri (Fiş) Değişim %", "LY Customer (Receipt) Change %"]);
add("units", ["Adet", "Net Adet", "Satış Adet", "Satış Adedi", "Satılan Adet", "Ürün Adedi QTY", "Units", "Net Units", "Units Sold", "Sold Units", "Product Units QTY", "Шт.", "Продано, шт.", "Sotilgan, dona", "Продано единиц", "Продано (нетто)", "Dona", "Sotilgan dona", "Sof dona"]);
add("lyUnits", ["LY Adet", "LY Net Adet", "LY Units", "LY Units Sold", "Шт. LY", "LY dona"]);
add("unitsChange", ["vs LY Adet", "LY Adet Değişim %", "LY Units Change %"]);
add("sales", ["Toplam Satış", "Total Sales", "Всего продаж", "Jami sotuv"]);
add("campaignSales", ["Kampanyalı Satış", "Campaign Sales", "Продажи по чекам с акцией", "Aksiyali cheklar sotuvi"]);
add("receipts", ["Toplam Müşteri (Fiş) Sayısı", "Total Customer (Receipt) Count", "Total Receipts"]);
add("campaignReceipts", ["Kampanyalı Müşteri (Fiş) Sayısı", "Kampanyalı Müşteri (Fiş)", "Campaign Receipts", "Campaign Customer (Receipt) Count", "Чеки с акцией", "Aksiyali cheklar"]);
add("units", ["Toplam Ürün Adedi QTY", "Total Product Units QTY", "Total Units"]);
add("campaignUnits", ["Kampanyalı Ürün Adedi QTY", "Kampanyalı Adet", "Campaign Product Units QTY", "Campaign Units", "Шт. с акцией", "Aksiyali dona"]);

export function workbookMetricHeading(original: string, language = "tr", productQuantity = false) {
  const year = original.match(/\s+(20\d{2})\s*$/)?.[1];
  const stem = original.replace(/\s+20\d{2}\s*$/, "").replace(/\s*(?:\(\s*UZS\s*\)|[, ]+UZS)\s*$/iu, "");
  // Product exports use Sales for quantity in English, not monetary revenue.
  const key = productQuantity && ["sales", "satış"].includes(matchKey(stem)) ? "units" : aliases.get(matchKey(stem));
  return withoutUzsLabel(key ? screenMetricColumnLabel(key, original, language) + (key === "salesChange" && year ? ` ${year}` : "") : original);
}

export function normalizeWorkbookMetricHeadings(bytes: Uint8Array, language = "tr", productQuantity = false): Uint8Array<ArrayBuffer> {
  const files = unzipSync(bytes);
  const references = new Map<string, string>();
  for (const [path, data] of Object.entries(files)) {
    if (!/^xl\/tables\/[^/]+\.xml$/.test(path)) continue;
    const source = strFromU8(data);
    const table = plain(/<table\b[^>]*\bname="([^"]+)"/.exec(source)?.[1] ?? "");
    if (!table) continue;
    for (const column of source.matchAll(/<tableColumn\b[^>]*\bname="([^"]*)"/g)) {
      const old = plain(column[1]), label = workbookMetricHeading(old, language, productQuantity);
      if (old !== label) references.set(`${table}[${old}]`, `${table}[${label}]`);
    }
  }
  const updateReferences = (source: string) => source.replace(/<(f|calculatedColumnFormula|totalsRowFormula)(\s[^>]*)?>([\s\S]*?)<\/\1>/g, (tag, name: string, attributes: string | undefined, encoded: string) => {
    const formula = plain(encoded);
    // Never rewrite a quoted Excel string, only actual structured references.
    const updated = formula.split(/("(?:[^"]|"")*")/g).map((part, index) => {
      if (index % 2) return part;
      for (const [old, label] of references) part = part.replaceAll(old, label);
      return part;
    }).join("");
    return formula === updated ? tag : `<${name}${attributes ?? ""}>${xml(updated)}</${name}>`;
  });
  let changed = false;
  for (const [path, data] of Object.entries(files)) {
    const sheet = /^xl\/worksheets\/sheet\d+\.xml$/.test(path);
    const table = /^xl\/tables\/[^/]+\.xml$/.test(path);
    if (!sheet && !table) continue;
    const source = strFromU8(data);
    let output = source;
    if (sheet) {
      // All native SRR360 exporters freeze through the last header row. This
      // includes the existing two-tier campaign heading without moving rows.
      const headerEnd = Number(/<pane\b[^>]*\bySplit="(\d+)"/.exec(source)?.[1] ?? 0);
      if (!headerEnd) continue;
      output = source.replace(/<row\b[^>]*\br="(\d+)"[^>]*>[\s\S]*?<\/row>/g, (row, rowNumber: string) => {
        if (Number(rowNumber) > headerEnd) return row;
        return row.replace(/<c\b[^>]*>[\s\S]*?<\/c>/g, cell => {
          if (!/\bt="inlineStr"/.test(cell) || /<f(?:\s|>)/.test(cell)) return cell;
          const texts = [...cell.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)];
          if (texts.length !== 1) return cell;
          const old = plain(texts[0][1]), label = workbookMetricHeading(old, language, productQuantity);
          return label === old ? cell : cell.replace(texts[0][0], texts[0][0].replace(texts[0][1], xml(label)));
        });
      });
    } else {
      output = source.replace(/<tableColumn\b[^>]*\bname="([^"]*)"[^>]*>/g, (tag, encoded: string) => {
        const old = plain(encoded), label = workbookMetricHeading(old, language, productQuantity);
        return label === old ? tag : tag.replace(`name="${encoded}"`, `name="${xml(label)}"`);
      });
    }
    output = updateReferences(output);
    if (output !== source) { files[path] = strToU8(output); changed = true; }
  }
  return new Uint8Array(changed ? zipSync(files, { level: 6 }) : bytes);
}
